import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, MapPin, Phone, Mail, User, ShieldCheck, 
  Edit3, Save, X, Globe, Award, FileText, ExternalLink, Upload 
} from 'lucide-react';
import './Profile.css';

const Profile = () => {
  const [formData, setFormData] = useState({
    systemId: '',
    collegeRegNo: '',
    universityName: '',
    collegeName: '',
    website: '',
    affiliation: '',
    streetAddress: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    collegePhone: '',
    collegeEmail: '',
    poName: '',
    poPhone: '',
    poEmail: '',
    tradeLicensePath: null,
    otherDocs: []
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});
  
  // New States for File Updates
  const [newTradeLicense, setNewTradeLicense] = useState(null);
  const [newOtherDocs, setNewOtherDocs] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/college/profile/${userId}`);
        setFormData(response.data);
        setOriginalData(response.data);
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };
    fetchProfileData();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    const userId = localStorage.getItem('userId');
    
    // We use FormData because we might be sending new files
    const updateData = new FormData();
    
    // 1. Append all text fields
    Object.keys(formData).forEach(key => {
      if (key !== 'otherDocs' && key !== 'tradeLicensePath') {
        updateData.append(key, formData[key]);
      }
    });

    // 2. Append new files if the user selected them
    if (newTradeLicense) updateData.append('tradeLicense', newTradeLicense);
    if (newOtherDocs) {
      Array.from(newOtherDocs).forEach(file => updateData.append('otherDocs', file));
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/college/update-profile/${userId}`, 
        updateData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      alert('Profile & Documents updated successfully!');
      setFormData(response.data.profile);
      setOriginalData(response.data.profile);
      setIsEditing(false);
      setNewTradeLicense(null);
      setNewOtherDocs(null);
    } catch (error) {
      console.error('Update failed:', error);
      alert('Failed to update profile.');
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setNewTradeLicense(null);
    setNewOtherDocs(null);
    setIsEditing(false);
  };

  return (
    <div className="profile-container">
      <div className="profile-header-banner">
        <div className="title-area">
          <h1 className="profile-title">Institution Profile</h1>
          <p className="profile-subtitle">System ID: {formData.systemId}</p>
        </div>
        <div className="form-actions">
          {!isEditing ? (
            <button className="btn-edit" onClick={() => setIsEditing(true)}><Edit3 size={18} /> Edit Profile</button>
          ) : (
            <div className="action-group">
              <button className="btn-cancel" onClick={handleCancel}><X size={18} /> Cancel</button>
              <button className="btn-save" onClick={handleSave}><Save size={18} /> Save Changes</button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-grid">
        
        {/* Section 1: College Identity */}
        <div className="form-card">
          <h2 className="card-title"><Building2 size={20}/> College Identity</h2>
          <div className="input-grid">
            <div className="input-box full">
              <label>College Name</label>
              <input name="collegeName" value={formData.collegeName || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>University</label>
              <input name="universityName" value={formData.universityName || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>Registration No.</label>
              <input value={formData.collegeRegNo || ''} readOnly className="locked" />
            </div>
            <div className="input-box">
              <label><Globe size={14}/> Website</label>
              <input name="website" value={formData.website || ''} readOnly={!isEditing} onChange={handleChange} placeholder="https://" />
            </div>
            <div className="input-box">
              <label><Award size={14}/> Affiliation</label>
              <input name="affiliation" value={formData.affiliation || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Section 2: Official Contact */}
        <div className="form-card">
          <h2 className="card-title"><Phone size={20}/> Official Contact</h2>
          <div className="input-grid">
            <div className="input-box">
              <label>College Phone</label>
              <input name="collegePhone" value={formData.collegePhone || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>College Email</label>
              <input name="collegeEmail" value={formData.collegeEmail || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box full">
              <label>Placement Officer Name</label>
              <input name="poName" value={formData.poName || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>PO Phone</label>
              <input name="poPhone" value={formData.poPhone || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>PO Email</label>
              <input name="poEmail" value={formData.poEmail || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Section 3: Location Details */}
        <div className="form-card">
          <h2 className="card-title"><MapPin size={20}/> Campus Location</h2>
          <div className="input-grid">
            <div className="input-box full">
              <label>Street Address</label>
              <input name="streetAddress" value={formData.streetAddress || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>City</label>
              <input name="city" value={formData.city || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>State</label>
              <input name="state" value={formData.state || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>Pincode</label>
              <input name="pincode" value={formData.pincode || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
            <div className="input-box">
              <label>Country</label>
              <input name="country" value={formData.country || ''} readOnly={!isEditing} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Section 4: Document Vault */}
        <div className="form-card">
          <h2 className="card-title"><FileText size={20}/> Document Vault</h2>
          <div className="document-stack">
            
            {/* Trade License Item */}
            <div className="doc-pill">
              <div className="doc-pill-info">
                <FileText size={18} />
                <div className="doc-meta">
                  <span>Trade License / Registration</span>
                  {newTradeLicense && <small className="pending-tag">New file selected</small>}
                </div>
              </div>
              <div className="doc-actions">
                {formData.tradeLicensePath && <a href={formData.tradeLicensePath} target="_blank" rel="noreferrer" className="view-link">View</a>}
                {isEditing && (
                  <label className="replace-btn">
                    <Upload size={14} /> Replace
                    <input type="file" hidden onChange={(e) => setNewTradeLicense(e.target.files[0])} />
                  </label>
                )}
              </div>
            </div>

            {/* Other Documents Logic */}
            <div className="doc-pill">
                <div className="doc-pill-info">
                  <FileText size={18} />
                  <div className="doc-meta">
                    <span>Additional Documents</span>
                    {newOtherDocs && <small className="pending-tag">{newOtherDocs.length} new files</small>}
                  </div>
                </div>
                <div className="doc-actions">
                  {isEditing && (
                    <label className="replace-btn">
                      <Upload size={14} /> Upload New
                      <input type="file" multiple hidden onChange={(e) => setNewOtherDocs(e.target.files)} />
                    </label>
                  )}
                </div>
            </div>
            {/* List existing other docs */}
            {!isEditing && formData.otherDocs && formData.otherDocs.map((url, i) => (
               <a key={i} href={url} target="_blank" rel="noreferrer" className="existing-other-link">
                 <ExternalLink size={12} /> View Document {i+1}
               </a>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;